// T034 — Search route: typeahead cohort search → navigate to /cohort/:cohortNumber
// T035 — wire this into App.tsx '/' route. The US2 agent owns App.tsx; coordinate via /lib/routes.tsx instead.

import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router";
import { useQuery } from "convex/react";
import { api } from "@convex-api";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string | undefined | null): string {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

const STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  confirmed: "Confirmed",
  "in-progress": "In Progress",
  completed: "Completed",
  cancelled: "Cancelled",
};

const STATUS_VARIANT: Record<
  string,
  "default" | "secondary" | "destructive" | "outline"
> = {
  draft: "outline",
  confirmed: "default",
  "in-progress": "default",
  completed: "secondary",
  cancelled: "destructive",
};

// ─── Component ────────────────────────────────────────────────────────────────

export function Search() {
  const [input, setInput] = useState("");
  const [debouncedInput, setDebouncedInput] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const navigate = useNavigate();
  const listRef = useRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounce input by 150 ms
  useEffect(() => {
    const id = setTimeout(() => setDebouncedInput(input.trim()), 150);
    return () => clearTimeout(id);
  }, [input]);

  const results = useQuery(
    api.cohorts.searchByCohortNumber,
    debouncedInput.length > 0 ? { q: debouncedInput } : "skip"
  );

  // Open dropdown when results arrive (or clear it when input empties)
  useEffect(() => {
    if (debouncedInput.length === 0) {
      setOpen(false);
      setActiveIndex(-1);
    } else if (results !== undefined) {
      setOpen(results.length > 0);
      setActiveIndex(-1);
    }
  }, [debouncedInput, results]);

  const goToCohort = useCallback(
    (cohortNumber: string) => {
      setOpen(false);
      setInput("");
      setDebouncedInput("");
      navigate(`/cohort/${encodeURIComponent(cohortNumber)}`);
    },
    [navigate]
  );

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open || !results || results.length === 0) {
      if (e.key === "Enter" && input.trim()) {
        // Direct navigation when there are no suggestions yet
        goToCohort(input.trim());
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => Math.min(prev + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0 && results[activeIndex]) {
        goToCohort(results[activeIndex].cohortNumber);
      } else if (results.length === 1) {
        goToCohort(results[0].cohortNumber);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
      setActiveIndex(-1);
    }
  }

  // Scroll active item into view
  useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    const el = listRef.current.children[activeIndex] as HTMLElement | undefined;
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-8">
      <Card className="w-full max-w-md overflow-visible">
        <CardHeader>
          <CardTitle className="text-xl">Open a cohort</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <Input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search cohort number (e.g. 611, -me, 06)"
              autoFocus
              autoComplete="off"
              className="h-10 text-base"
              aria-label="Search cohorts"
              aria-autocomplete="list"
              aria-expanded={open}
              aria-controls="cohort-suggestions"
              aria-activedescendant={
                activeIndex >= 0 ? `suggestion-${activeIndex}` : undefined
              }
              role="combobox"
            />

            {open && results && results.length > 0 && (
              <ul
                id="cohort-suggestions"
                ref={listRef}
                role="listbox"
                aria-label="Cohort suggestions"
                className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-md overflow-auto max-h-72 py-1"
              >
                {results.map((cohort, i) => (
                  <li
                    key={cohort.id}
                    id={`suggestion-${i}`}
                    role="option"
                    aria-selected={i === activeIndex}
                    className={[
                      "flex flex-col gap-0.5 cursor-pointer px-3 py-2 text-sm select-none",
                      i === activeIndex
                        ? "bg-accent text-accent-foreground"
                        : "hover:bg-accent hover:text-accent-foreground",
                    ].join(" ")}
                    onMouseEnter={() => setActiveIndex(i)}
                    onMouseDown={(e) => {
                      // mousedown fires before input blur; prevent blur first
                      e.preventDefault();
                      goToCohort(cohort.cohortNumber);
                    }}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold">{cohort.cohortNumber}</span>
                      <Badge
                        variant={STATUS_VARIANT[cohort.status] ?? "outline"}
                        className="text-xs shrink-0"
                      >
                        {STATUS_LABELS[cohort.status] ?? cohort.status}
                      </Badge>
                    </div>
                    {cohort.programName && (
                      <span className="text-muted-foreground text-xs truncate">
                        {cohort.programName}
                      </span>
                    )}
                    {(cohort.startDate || cohort.endDate) && (
                      <span className="text-muted-foreground text-xs">
                        {"📅"} {formatDate(cohort.startDate)}
                        {cohort.endDate ? ` → ${formatDate(cohort.endDate)}` : ""}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {debouncedInput.length > 0 && results?.length === 0 && (
              <p className="mt-2 text-sm text-muted-foreground">
                No cohorts matching &ldquo;{debouncedInput}&rdquo;
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
